import { makeStyles } from '@material-ui/core/styles'
import PropTypes from 'prop-types'
import React, { useEffect, useState } from 'react'

import { useAuth } from 'src/modules/auth'
import { useActionRunner } from 'src/modules/notification'
import { Container, SearchInput } from 'src/modules/ui'

import { usePurchasing } from '../../purchasing/hooks'
import { useSuppliers } from '../hooks'
import SupplierLocation from './SupplierLocation'
import SuppliersList from './SuppliersList'
import SupplierFilters from './SupplierFilters'

const useStyles = makeStyles(theme => ({
  root: {
    display: 'flex',
    flexDirection: 'column',
    [theme.breakpoints.up('tablet')]: {
      flexDirection: 'row',
    },
  },
  filterPanel: {
    [theme.breakpoints.up('tablet')]: {
      marginRight: theme.spacing(2),
      width: 220,
    },
  },
  rightBox: {
    flexGrow: 1,
  },
  searchInput: {
    [theme.breakpoints.up('mobile')]: {
      marginLeft: theme.spacing(2),
    },
  },
  toolbar: {
    marginBottom: theme.spacing(2),
    display: 'flex',
    flexDirection: 'column',
    '& > *': {
      marginBottom: theme.spacing(1),
    },
    '& > *:last-child': {
      marginBottom: 0,
    },
    [theme.breakpoints.up('mobile')]: {
      alignItems: 'center',
      flexDirection: 'row',
      justifyContent: 'space-between',
      '& > *': {
        marginBottom: theme.spacing(0),
      },
    },
  },
}))

export const SuppliersView = ({
  fetching,
  lastPageWasSupplier,
  onFetchSuppliers,
  section,
  suppliers,
  ...props
}) => {
  const classes = useStyles()
  const [firstLoad, setFirstLoad] = useState(true)
  const [postcode, setPostcode] = useState()
  const [fetchingMore, setFetchingMore] = useState()
  const {
    category,
    subcategory,
    allergenFilters,
    dietaryFilters,
    sustainabilityFilters,
    search,
    setSearch,
    setNearEnvironment,
    nearEnvironment,
    setNearPostcode,
    nearPostcode,
    setLastListScrollOffset,
  } = useSuppliers()
  const { purchasingEnvironments } = usePurchasing()

  useEffect(() => {
    if (nearEnvironment) {
      const env = purchasingEnvironments.find(e => e.id === nearEnvironment)
      setPostcode(env.address.postcode)
    } else if (nearPostcode) {
      setPostcode(nearPostcode)
    } else {
      setPostcode()
    }
  }, [nearEnvironment, nearPostcode])

  const fetchPage = (page = 1) => {
    onFetchSuppliers({
      section,
      page,
      category,
      subcategory,
      allergens: allergenFilters,
      dietary: dietaryFilters,
      sustainability: sustainabilityFilters,
      search,
      postcode,
    })
  }

  useEffect(() => {
    // If this previous page is a supplier page, then we want to render the
    // list as it was previously as they probably clicked the back button.
    // Also check that this is the same section we were last in
    if (firstLoad && lastPageWasSupplier && suppliers.section === section) {
      setFirstLoad(false)
      window.scrollTo(0, suppliers.lastListScrollOffset)
      return
    }
    fetchPage(1)
    setFirstLoad(false)
  }, [
    category,
    subcategory,
    allergenFilters,
    dietaryFilters,
    sustainabilityFilters,
    search,
    postcode,
  ])

  const handleFetchMore = async () => {
    if (suppliers.hasMore) {
      setFetchingMore(true)
      await fetchPage(suppliers.page + 1)
      setFetchingMore(true)
    }
  }

  const saveScrollPosition = () => {
    setLastListScrollOffset(window.pageYOffset)
  }

  return (
    <Container className={classes.root}>
      <SupplierFilters className={classes.filterPanel} />
      <div className={classes.rightBox}>
        <div className={classes.toolbar}>
          <SupplierLocation
            environments={purchasingEnvironments}
            environment={nearEnvironment}
            onChangeEnvironment={setNearEnvironment}
            onChangePostcode={setNearPostcode}
            postcode={nearPostcode}
          />
          <SearchInput
            className={classes.searchInput}
            placeholder="Search supplier name..."
            size="large"
            value={search}
            onChange={setSearch}
          />
        </div>
        <SuppliersList
          hasNextPage={suppliers.hasMore}
          fetching={fetching && !fetchingMore}
          onClickSupplier={saveScrollPosition}
          onFetchMore={handleFetchMore}
          suppliers={suppliers.results}
          total={suppliers.total}
          {...props}
        />
      </div>
    </Container>
  )
}

SuppliersView.propTypes = {
  fetching: PropTypes.bool,
  lastPageWasSupplier: PropTypes.bool,
  onFetchSuppliers: PropTypes.func,
  suppliers: PropTypes.object,
}

const SuppliersViewContainer = props => {
  const { lastPageWasSupplier, fetchSuppliers, suppliers } = useSuppliers()
  const { hasPermission } = useAuth()

  // Set initial fetch status to false only if we are trying to render previous
  // supplier list
  const [fetchData, fetching] = useActionRunner(fetchSuppliers, {
    initialFetchingState: !lastPageWasSupplier,
  })

  return (
    <SuppliersView
      lastPageWasSupplier={lastPageWasSupplier}
      onFetchSuppliers={fetchData}
      canRequestAgreement={hasPermission('source_suppliers')}
      canStar={hasPermission('source_suppliers')}
      fetching={fetching}
      suppliers={suppliers}
      {...props}
    />
  )
}

export default SuppliersViewContainer
